import { get as getCookie, set as setCookie } from 'js-cookie';


const ANNOUNCEMENT_BANNER_CLOSED_COOKIE_NAME = 'rh-announcement-banner-closed';
const ANNOUNCEMENT_BANNER_COOKIE_EXPIRY_DAYS = 5;

/**
 * Retrieves the properties for displaying an announcement banner.
 * @param {object} announcementBanner - The announcement banner object.
 * @param {number} announcementBanner.id - The unique identifier for the banner.
 * @param {string} announcementBanner.color - The color of the banner ('green', 'yellow', 'blue').
 * @param {string} announcementBanner.fontSize - The font size of the banner ('lg', 'md', 'sm').
 * @param {string} announcementBanner.title - The title of the announcement banner.
 * @param {string} announcementBanner.defaultCopy - The default message copy for the banner.
 * @param {string[]} announcementBanner.provinces - An array of province codes where the banner should be displayed.
 * @param {Object.<string, string>} [announcementBanner.provincialCopyOverrides] - An optional object mapping province codes to custom messages.
 * @param {string} province - The province code of the user.
 * @returns {object | false} The banner properties if it should be displayed, otherwise `false`.
 * @property {string} callerName - The name of the component using the banner.
 * @property {string} variant - The banner variant ('success', 'alert', 'general').
 * @property {string} title - The title of the announcement banner.
 * @property {string} message - The announcement banner message.
 * @property {string} messageSize - The message size ('large', 'medium', 'small').
 * @property {boolean} isCloseVisible - Whether the close button is visible.
 * @property {Function} onHide - Callback function when the banner is closed.
 * @property {string} className - The CSS class name for styling the banner.
 */
function getAnnouncementBannerProps(announcementBanner, province) {
    // Check if the banner was previously closed using cookies
    const closedAnnouncementBannerCookieName = `${ANNOUNCEMENT_BANNER_CLOSED_COOKIE_NAME}-${announcementBanner.id}`;
    const closedAnnouncementBanner = getCookie(closedAnnouncementBannerCookieName);

    if (closedAnnouncementBanner) {
        return false; // Do not show the banner if it was closed before
    }

    // If the banner is restricted to specific provinces and the user's province is not included, exit
    if (announcementBanner.provinces.length !== 0 && !announcementBanner.provinces.includes(province)) {
        return false;
    }

    // Determine the banner variant (color)
    const bannerColor = announcementBanner.color;
    const bannerVariant
        = bannerColor === 'green'
            ? 'success'
            : bannerColor === 'yellow'
                ? 'alert'
                : 'general'; // Default to 'general' for blue color

    // Determine the font size variant
    const bannerFontSize = announcementBanner.fontSize;
    const bannerMessageSize
        = bannerFontSize === 'lg'
            ? 'large'
            : bannerFontSize === 'sm'
                ? 'small'
                : 'medium'; // Default to 'medium' for 'md' size

    // Retrieve the appropriate copy for the banner based on the province
    const copyOverrides = announcementBanner.provincialCopyOverrides;
    const bannerCopy = copyOverrides?.[province] || announcementBanner.defaultCopy;

    // return the banner props
    return {
        callerName: 'Layout',
        variant: bannerVariant,
        message: bannerCopy,
        messageSize: bannerMessageSize,
        isCloseVisible: true,
        onHide: () => {
            // Store in a cookie that the banner was closed
            setCookie(
                closedAnnouncementBannerCookieName,
                true,
                { expires: ANNOUNCEMENT_BANNER_COOKIE_EXPIRY_DAYS }
            );
        },
        className: 'rh-announcement-banner',
        messageBannerStyle: 'compact',
        closeButtonSize: 'S',
    };
}

export default getAnnouncementBannerProps;
